import * as Dialog from '@radix-ui/react-dialog';
import { Check, CircleAlert, LoaderCircle } from 'lucide-react';
import type { ReactNode, SVGProps } from 'react';
import type { ServerStatus } from '@harness/shared';
import { useUI } from '../lib/store';

export function Spinner({ className = '' }: { className?: string }) {
  return <LoaderCircle className={`icon spin ${className}`} aria-label="Working" />;
}

/** GitHub mark (brand icons aren't shipped by lucide). */
export function GithubIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="icon" aria-hidden {...p}>
      <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
      <path d="M9 18c-4.51 2-5-2-7-2" />
    </svg>
  );
}

/** Placeholder Jira glyph (swap for the official Atlassian mark). */
export function JiraIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="icon" aria-hidden {...p}>
      <path d="M12 2 2 12l10 10 10-10z" />
      <path d="M12 8l-4 4 4 4 4-4z" />
    </svg>
  );
}

/** Recite Me wordmark, tinted red by the theme's --logo-filter (white = untinted, for dark grounds). */
export function Logo({ height = 26, white = false }: { height?: number; white?: boolean }) {
  return <img src="/reciteme-logo.png" alt="Recite Me" style={{ height, width: 'auto', display: 'block', alignSelf: 'center', flex: 'none', filter: white ? 'none' : 'var(--logo-filter)' }} />;
}

export const statusLabel = (s: ServerStatus | undefined | null) => (s === 'online' ? 'Online' : s === 'unreachable' ? 'Unreachable' : 'Offline');

export function StatusDot({ status, size }: { status: ServerStatus | undefined | null; size?: 'sm' | 'lg' }) {
  return <span className={`dot dot-${status ?? 'offline'} ${size ?? ''}`} aria-label={statusLabel(status)} />;
}

export function Switch({ on, onChange, disabled, label }: { on: boolean; onChange?: (v: boolean) => void; disabled?: boolean; label?: string }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} className="switch" disabled={disabled} onClick={() => onChange?.(!on)} />;
}

export function SwitchRow({ on, onChange, children, disabled }: { on: boolean; onChange: (v: boolean) => void; children: ReactNode; disabled?: boolean }) {
  return (
    <label className="switch-row" style={{ opacity: disabled ? 0.45 : 1 }}>
      <Switch on={on} onChange={onChange} disabled={disabled} />
      <span>{children}</span>
    </label>
  );
}

export function Seg<T extends string>({
  value,
  options,
  onChange,
  size,
  tone,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  size?: 'sm';
  tone?: (v: T) => string;
}) {
  return (
    <div className={`seg ${size ?? ''}`} role="group">
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} className={o.value === value ? tone?.(o.value) : undefined} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Check2({ on }: { on: boolean }) {
  return <span className={`check ${on ? 'on' : ''}`}>{on ? '✓' : ''}</span>;
}

export function Modal({
  open,
  onClose,
  children,
  width = 440,
  className = 'dialog',
  label,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  width?: number | string;
  className?: string;
  label: string;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-backdrop" />
        <Dialog.Content
          className={className}
          aria-describedby={undefined}
          style={{ position: 'fixed', zIndex: 51, left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: typeof width === 'number' ? `min(${width}px, calc(100vw - 32px))` : width }}
        >
          <Dialog.Title className="sr-only">{label}</Dialog.Title>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function Toast() {
  const t = useUI((s) => s.toast);
  if (!t) return null;
  return (
    <div className={`toast ${t.kind}`} role="status" aria-live="polite" key={t.id}>
      <span className="toast-icon">{t.kind === 'error' ? <CircleAlert className="icon" /> : <Check className="icon" />}</span>
      <span>{t.text}</span>
    </div>
  );
}

export function Micro({ children, style }: { children: ReactNode; style?: React.CSSProperties }) {
  return (
    <div className="micro" style={style}>
      {children}
    </div>
  );
}

export function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <Micro>{label}</Micro>
      <div className="ellipsis">{children}</div>
    </div>
  );
}

export function PageHeader({ title, sub, children }: { title: ReactNode; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 240 }}>
        <h1>{title}</h1>
        {sub && <div className="muted" style={{ marginTop: 4 }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}
